import { useTranslation } from 'react-i18next'

import { formatDate, formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import {
  CalendarIcon,
  DestinationPinIcon,
  OriginPinIcon,
  PaymentIcon,
  TruckIcon,
} from '#/shared/ui/icons'

import { formatPaymentParts } from '../model/format-payment'
import type { CarrierRequestsOrderSummary } from '../model/types'

interface OrderSummaryCardProps {
  order: CarrierRequestsOrderSummary
}

export function OrderSummaryCard({ order }: OrderSummaryCardProps) {
  return (
    <div className="border-b border-gray-200 p-2.5">
      <div className="flex flex-col gap-3.5 rounded-md bg-warm-25 px-5 py-4">
        <OrderHead order={order} />
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
          <div className="lg:w-47.75 lg:shrink-0">
            <RouteCell order={order} />
          </div>
          <div className="lg:w-47.75 lg:shrink-0">
            <VehicleCell order={order} />
          </div>
          <div className="lg:w-35 lg:shrink-0">
            <DatesCell order={order} />
          </div>
          <div className="lg:w-35 lg:shrink-0">
            <PriceCell order={order} />
          </div>
          <div className="lg:w-30 lg:shrink-0">
            <PaymentCell order={order} />
          </div>
        </div>
      </div>
    </div>
  )
}

function OrderHead({ order }: { order: CarrierRequestsOrderSummary }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-base font-semibold text-gray-900">
        {t('carrierRequests.orderNumber', { number: order.orderNumber })}
      </span>
      <span
        className={cn(
          'inline-flex h-5 items-center rounded-md bg-blue-50 px-2 text-xs text-primary',
        )}
      >
        {order.status}
      </span>
      <TruckIcon className="size-4 text-gray-500" />
    </div>
  )
}

function RouteCell({ order }: { order: CarrierRequestsOrderSummary }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
        <OriginPinIcon className="size-3.5 text-gray-500" />
        <span>{order.origin}</span>
      </div>
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
        <DestinationPinIcon className="size-3.5 text-gray-500" />
        <span>{order.destination}</span>
      </div>
      {order.distanceMiles !== undefined && (
        <span className="pl-5 text-xs text-gray-500">
          {t('carrierRequests.distanceMiles', { miles: order.distanceMiles })}
        </span>
      )}
    </div>
  )
}

function VehicleCell({ order }: { order: CarrierRequestsOrderSummary }) {
  if (!order.vehicleLabel) return <div />
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-gray-900">
        {order.vehicleLabel}
      </span>
      {order.vehicleType && (
        <span className="text-xs text-gray-500">{order.vehicleType}</span>
      )}
    </div>
  )
}

function DatesCell({ order }: { order: CarrierRequestsOrderSummary }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1 text-sm">
      <DateRow
        label={t('carrierRequests.pickup')}
        value={order.pickupAt ? formatDate(order.pickupAt) : undefined}
      />
      <DateRow
        label={t('carrierRequests.delivery')}
        value={order.deliveryAt ? formatDate(order.deliveryAt) : undefined}
      />
    </div>
  )
}

function DateRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-900">{value ?? '—'}</span>
      <span className="text-xs text-gray-500">·</span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  )
}

function PriceCell({ order }: { order: CarrierRequestsOrderSummary }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">
          {formatUsd(order.totalTariff)}
        </span>
        <span className="text-xs text-gray-500">·</span>
        <span className="text-xs text-gray-500">
          {t('carrierRequests.totalTariff')}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">
          {formatUsd(order.carrierPay)}
        </span>
        <span className="text-xs text-gray-500">·</span>
        <span className="text-xs text-gray-500">
          {t('carrierRequests.carrierPay')}
        </span>
      </div>
    </div>
  )
}

function PaymentCell({ order }: { order: CarrierRequestsOrderSummary }) {
  const { t } = useTranslation()
  const parts = formatPaymentParts(t, order.paymentMethod, order.paymentTerms)
  return (
    <div className="flex flex-col gap-1 text-sm">
      {parts.length > 0 && (
        <div className="flex items-center gap-1.25 text-gray-900">
          <PaymentIcon className="size-3.5 text-gray-500" />
          <span>{parts.join(' · ')}</span>
        </div>
      )}
      {order.postedAt && (
        <div className="flex items-center gap-1.25 text-xs text-gray-500">
          <CalendarIcon className="size-3.5" />
          <span>
            {t('carrierRequests.postedDate', {
              date: formatDate(order.postedAt),
            })}
          </span>
        </div>
      )}
    </div>
  )
}
